// Thin REST client over the Story Editor backend.
// Backend lives under /api (proxied by Vite in dev, by Express in prod).

const BASE = import.meta.env.VITE_API_BASE || '/api';

// Subscribers notified when an API call returns 401 — so the app can redirect to /login.
const unauthHandlers = new Set();
export function onUnauthorized(fn) {
  unauthHandlers.add(fn);
  return () => unauthHandlers.delete(fn);
}

async function request(method, path, { body, formData, query, timeoutMs } = {}) {
  let url = `${BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams(Object.entries(query).filter(([, v]) => v != null && v !== ''));
    if ([...qs].length) url += `?${qs}`;
  }
  const opts = {
    method,
    headers: {},
    credentials: 'include',
  };
  if (formData) {
    opts.body = formData;
  } else if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const controller = timeoutMs ? new AbortController() : null;
  const timer = controller && setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(url, { ...opts, signal: controller?.signal });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('Сервер не ответил вовремя — попробуй ещё раз');
    }
    if (err?.message === 'Failed to fetch' || err?.name === 'TypeError') {
      throw new Error('Нет связи с сервером (fetch failed) — обнови страницу или подожди минуту');
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (res.status === 401 && !path.startsWith('/auth/')) {
    for (const fn of unauthHandlers) { try { fn(); } catch {} }
  }
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error?.message || `${method} ${path} failed (${res.status})`);
    err.code = data?.error?.code;
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  // Auth
  authStatus: () => request('GET', '/auth/status'),
  login: (password) => request('POST', '/auth/login', { body: { password } }),
  logout: () => request('POST', '/auth/logout'),

  // Settings
  getSettings: () => request('GET', '/settings'),
  updateSettings: (body) => request('POST', '/settings', { body }),
  getFonts: () => request('GET', '/settings/fonts'),

  // Renders
  listRenders: (params) => request('GET', '/renders', { query: params }),
  rendersSummary: () => request('GET', '/renders/summary'),
  cancelRender: (id) => request('POST', `/renders/${id}/cancel`),
  retryRender: (id) => request('POST', `/renders/${id}/retry`),

  // Episodes
  listEpisodes: (params) => request('GET', '/episodes', { query: params }),
  getEpisode: (id) => request('GET', `/episodes/${id}`),
  // Lightweight transcript metadata (counts + RU availability), no word arrays.
  getTranscript: (id) => request('GET', `/episodes/${id}/transcript`),
  // Minimal words ({ text, start, end }) for one language, loaded on demand.
  getTranscriptWords: (id, lang = 'es') =>
    request('GET', `/episodes/${id}/transcript/words`, { query: { lang } }),
  uploadEpisode: (formData) => request('POST', '/episodes/upload', { formData }),
  episodeFromFb: (body) => request('POST', '/episodes/from-fb', { body }),
  retryEpisode: (id) => request('POST', `/episodes/${id}/retry`),
  updateEpisode: (id, patch) => request('PATCH', `/episodes/${id}`, { body: patch }),
  videoUrl: (id) => `${BASE}/episodes/${id}/video`,
  episodeThumbnailUrl: (id) => `${BASE}/episodes/${id}/thumbnail`,
  clipThumbnailUrl: (id) => `${BASE}/clips/${id}/thumbnail`,

  // Chapters
  updateChapter: (id, patch) => request('PATCH', `/chapters/${id}`, { body: patch }),

  // Clips
  listClips: (params) => request('GET', '/clips', { query: params }),
  getClip: (id) => request('GET', `/clips/${id}`),
  createClip: (body) => request('POST', '/clips', { body }),
  refreshSubtitles: (id) => request('POST', `/clips/${id}/refresh-subtitles`),
  updateClip: (id, patch) => request('PATCH', `/clips/${id}`, { body: patch }),
  deleteClip: (id) => request('DELETE', `/clips/${id}`),
  renderClip: (id) => request('POST', `/clips/${id}/render`),
  renderStatus: (id) => request('GET', `/clips/${id}/render-status`),
  translateAllEn: (id, body) => request('POST', `/clips/${id}/translate-all-en`, { body: body || {}, timeoutMs: 180_000 }),
  generateCaption: (id) => request('POST', `/clips/${id}/generate-caption`, { body: {}, timeoutMs: 180_000 }),

  clipPreviewUrl: (id) => `${BASE}/clips/${id}/preview.mp4`,

  // Subtitles
  createSubtitle: (body) => request('POST', '/subtitles', { body }),
  updateSubtitle: (id, patch) => request('PATCH', `/subtitles/${id}`, { body: patch }),
  deleteSubtitle: (id) => request('DELETE', `/subtitles/${id}`),
  splitSubtitle: (id, body) => request('POST', `/subtitles/${id}/split`, { body }),
  mergeSubtitles: (ids) => request('POST', '/subtitles/merge', { body: { ids } }),
  translateSubtitleEn: (id) => request('POST', `/subtitles/${id}/translate-en`, { body: {}, timeoutMs: 120_000 }),

  // Publish
  schedulePublish: (body) => request('POST', '/publish', { body }),
  publishQueue: (params) => request('GET', '/publish/queue', { query: params }),
  publishActiveForClip: (clipId) => request('GET', '/publish/queue', { query: { clip_id: clipId, active: '1' } }),
  cancelPublish: (id) => request('POST', `/publish/${id}/cancel`),
  retryPublish: (id) => request('POST', `/publish/${id}/retry`),

  // Jobs
  jobs: () => request('GET', '/jobs'),

  // Topics
  topics: () => request('GET', '/topics'),

  // Health
  health: () => request('GET', '/health'),
};
